import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowRight, FiClock, FiUsers, FiFileText, FiDollarSign,
  FiInfo, FiBookOpen, FiMapPin, FiCheckCircle, FiAlertCircle, FiCopy, FiShare2,
  FiEye,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { salvarRedirecionamento } from '../../lib/redirectPosLogin';
import { cartaServicosService, type ServicoDetalhe } from '../../services/cartaServicos.service';

const FORMA_LABEL: Record<string, string> = {
  presencial: 'Presencial',
  online: 'Online',
  ambos: 'Presencial ou online',
};

export function ServicoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, cadastroCompleto } = useAuth();

  const [dados, setDados] = useState<ServicoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!id) return;
    setCarregando(true);
    cartaServicosService.buscarDetalheCarta(id)
      .then((d) => {
        if ('erro' in d) setErro(d.erro);
        else setDados(d);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [id]);

  function copiarLink() {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  async function compartilhar() {
    if (!dados) return;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: dados.servico.nome,
          text: dados.servico.descricao ?? `Serviço da ${dados.estabelecimento.nome}`,
          url: window.location.href,
        });
      } catch { /* cancelado */ }
    } else {
      copiarLink();
    }
  }

  if (carregando) {
    return (
      <PortalPage titulo="Serviço" icone={<FiInfo size={26} />} largura="media" voltarHref="/servicos" voltarLabel="Carta de serviços">
        <div className="flex justify-center py-20"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erro || !dados) {
    return (
      <PainelResultado
        titulo="Serviço não encontrado"
        tom="aviso"
        icone={<FiAlertCircle size={32} />}
        descricao={
          erro === 'servico_nao_encontrado'
            ? 'Este serviço não está mais disponível ou nunca existiu.'
            : erro
        }
        acoes={
          <Button variant="outline" onClick={() => navigate('/servicos')}>
            Voltar à carta de serviços
          </Button>
        }
      />
    );
  }

  const { servico, area } = dados;
  // protocolo_cidadao e esic exigem identificação; ouvidoria não.
  const exigeIdentificacao = ['protocolo_cidadao', 'esic'].includes(servico.modulo_codigo);
  const ehOuvidoria = servico.modulo_codigo === 'ouvidoria';

  const linkSolicitar = ehOuvidoria
    ? '/ouvidoria'
    : servico.modulo_codigo === 'esic'
      ? '/esic'
      : `/solicitar/${servico.id}`;

  // Estados do CTA: sem login → entrar; logado sem cadastro → completar; ok → solicitar.
  const precisaLogin = exigeIdentificacao && !session;
  const precisaCompletar = exigeIdentificacao && !!session && !cadastroCompleto;

  function solicitar() {
    if (ehOuvidoria) { navigate(linkSolicitar); return; }
    if (precisaLogin) {
      salvarRedirecionamento(linkSolicitar);
      navigate('/login', { state: { de: linkSolicitar } });
      return;
    }
    if (precisaCompletar) {
      salvarRedirecionamento(linkSolicitar);
      navigate('/completar-cadastro');
      return;
    }
    navigate(linkSolicitar);
  }

  const rotuloCta = ehOuvidoria
    ? 'Acessar Ouvidoria'
    : precisaLogin
      ? 'Entrar para solicitar'
      : precisaCompletar
        ? 'Completar cadastro'
        : 'Solicitar este serviço';

  return (
    <PortalPage
      titulo={servico.nome}
      subtitulo={servico.descricao ?? undefined}
      icone={<FiInfo size={26} />}
      largura="media"
      voltarHref="/servicos"
      voltarLabel="Carta de serviços"
      breadcrumbs={area ? [{ label: 'Serviços', href: '/servicos' }, { label: area.nome }] : undefined}
      acoes={
        <>
          <button
            onClick={compartilhar}
            title="Compartilhar"
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
          >
            <FiShare2 size={15} /> {typeof navigator.share === 'function' ? 'Compartilhar' : 'Copiar link'}
          </button>
          <Button onClick={solicitar}>
            {rotuloCta} <FiArrowRight size={14} />
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {copiado && (
          <Alert variante="sucesso">
            <FiCheckCircle size={12} className="inline mr-1" /> Link copiado para a área de transferência.
          </Alert>
        )}

        {precisaLogin && (
          <Alert variante="info">
            Para solicitar este serviço,{' '}
            <Link to="/login" state={{ de: linkSolicitar }} className="font-medium underline">entre</Link> ou{' '}
            <Link to="/cadastro" state={{ de: linkSolicitar }} className="font-medium underline">crie sua conta</Link>.
          </Alert>
        )}
        {precisaCompletar && (
          <Alert variante="aviso">
            Para solicitar este serviço você precisa{' '}
            <Link to="/completar-cadastro" className="font-medium underline">completar seu cadastro</Link>.
          </Alert>
        )}

        {/* Atributos rápidos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {servico.prazo_dias != null && (
            <CardAtributo icone={<FiClock size={16} />} titulo="Prazo de atendimento">
              {servico.prazo_dias} dia(s) útil(eis)
            </CardAtributo>
          )}
          {servico.forma_prestacao && (
            <CardAtributo icone={<FiMapPin size={16} />} titulo="Forma de prestação">
              {FORMA_LABEL[servico.forma_prestacao] ?? servico.forma_prestacao}
            </CardAtributo>
          )}
          {servico.publico_alvo && (
            <CardAtributo icone={<FiUsers size={16} />} titulo="Público-alvo">
              {servico.publico_alvo}
            </CardAtributo>
          )}
        </div>

        {/* Descrição completa */}
        {servico.descricao_completa && (
          <SectionCard titulo="Sobre o serviço" icone={<FiInfo size={18} />}>
            <div
              className="prose prose-slate prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: servico.descricao_completa }}
            />
          </SectionCard>
        )}

        {/* Documentos necessários */}
        {servico.documentos_necessarios.length > 0 && (
          <SectionCard titulo="Documentos necessários" icone={<FiFileText size={18} />}>
            <ul className="space-y-2">
              {servico.documentos_necessarios.map((doc, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <FiCheckCircle className="text-institutional flex-shrink-0 mt-0.5" size={14} />
                  <span>{doc}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {/* Custos */}
        {servico.custos.length > 0 && (
          <SectionCard titulo="Custos" icone={<FiDollarSign size={18} />}>
            <ul className="divide-y divide-slate-100">
              {servico.custos.map((c, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{c.item}</div>
                    {c.observacao && <div className="text-xs text-slate-500">{c.observacao}</div>}
                  </div>
                  <div className="text-sm tabular-nums text-slate-900 flex-shrink-0">
                    {c.isento
                      ? <span className="text-emerald-600 font-medium">Isento</span>
                      : <span className="font-medium">{c.valor}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {/* Unidades de atendimento (item 2.53) */}
        {dados.unidades.length > 0 && (
          <SectionCard titulo="Unidades de atendimento" icone={<FiMapPin size={18} />}>
            <p className="text-sm text-slate-600 mb-3">
              O atendimento deste serviço é realizado {dados.unidades.length === 1 ? 'na unidade' : 'nas unidades'} abaixo:
            </p>
            <ul className="space-y-2">
              {dados.unidades.map((u) => (
                <li key={u.id} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3">
                  <FiMapPin className="text-institutional flex-shrink-0 mt-0.5" size={14} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{u.nome}</div>
                    {(u.endereco || u.bairro) && (
                      <div className="text-xs text-slate-600">{[u.endereco, u.bairro].filter(Boolean).join(' · ')}</div>
                    )}
                    {u.telefone && <div className="text-xs text-slate-500">Tel.: {u.telefone}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {/* Como acompanhar */}
        {servico.como_acompanhar && (
          <SectionCard titulo="Como acompanhar" icone={<FiEye size={18} />}>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{servico.como_acompanhar}</div>
          </SectionCard>
        )}

        {/* Local + horário */}
        {(servico.local_atendimento || servico.horario_atendimento) && (
          <SectionCard titulo="Atendimento presencial" icone={<FiMapPin size={18} />}>
            {servico.local_atendimento && (
              <div className="text-sm text-slate-700 mb-1">
                <strong>Local:</strong> {servico.local_atendimento}
              </div>
            )}
            {servico.horario_atendimento && (
              <div className="text-sm text-slate-700">
                <strong>Horário:</strong> {servico.horario_atendimento}
              </div>
            )}
          </SectionCard>
        )}

        {/* Legislacao */}
        {servico.legislacao && (
          <SectionCard titulo="Legislação aplicável" icone={<FiBookOpen size={18} />}>
            <div className="text-xs text-slate-700 whitespace-pre-wrap">{servico.legislacao}</div>
          </SectionCard>
        )}

        {/* CTA final */}
        {!ehOuvidoria && (
          <SectionCard className="bg-slate-50">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Pronto para solicitar?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  {precisaLogin
                    ? 'Entre ou crie sua conta para abrir uma solicitação.'
                    : precisaCompletar
                      ? 'Complete seu cadastro para abrir uma solicitação.'
                      : 'Abra sua solicitação agora e acompanhe pelo portal.'}
                </p>
              </div>
              <Button onClick={solicitar}>
                {rotuloCta} <FiArrowRight size={14} />
              </Button>
            </div>
          </SectionCard>
        )}

        {/* Botão copiar URL embutido */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Compartilhar este serviço:</span>
          <button
            onClick={copiarLink}
            className="inline-flex items-center gap-1 text-institutional hover:underline"
          >
            <FiCopy size={11} /> Copiar URL
          </button>
        </div>
      </div>
    </PortalPage>
  );
}

function CardAtributo({ icone, titulo, children }: { icone: React.ReactNode; titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
      <div className="flex items-start gap-2">
        <div className="text-slate-400 flex-shrink-0 mt-0.5">{icone}</div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500">{titulo}</div>
          <div className="text-sm font-medium text-slate-900 mt-0.5">{children}</div>
        </div>
      </div>
    </div>
  );
}
